import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menu } from "lucide-react";
import { Sidebar, SidebarContent } from "@/shared/components/Sidebar";
import { UserMenu } from "@/shared/components/UserMenu";
import { UserSettingSync } from "@/shared/components/UserSettingSync";
import { NotificationBell } from "@/features/notification/ui/notification-bell";
import { Button } from "@/shared/ui/button";
import { Logo } from "@/shared/ui/logo";
import { Sheet, SheetContent, SheetTitle } from "@/shared/ui/sheet";
import { useLanguage } from "@/shared/hooks/use-language";
import { ScopeContextStorage } from "@/shared/lib/scope-storage";

const DESKTOP_MEDIA_QUERY = "(min-width: 768px)";

export const ConsoleLayout = () => {
  const { t } = useLanguage();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const pendingExitRef = useRef<{ to: string; state?: unknown } | null>(null);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // The console is only meaningful inside a complete scope, so an incomplete one is routed
  // onward from here rather than rendered. This guard used to live in the `/console` page,
  // which left every other `/console/*` route unguarded — the sidebar could then render a
  // menu for a scope nobody had. Owning it once, above the Outlet, is also what keeps it
  // from racing: two components deciding the same redirect navigate twice.
  useEffect(() => {
    // Both halves come from storage alone. `history.state` survives a reload, so router
    // state still names a session and a scope after the record has changed or been cleared,
    // and trusting it here would route on a scope nobody is in. The pickers write the record
    // before they navigate, so storage already has everything router state would carry.
    const currentSessionId = ScopeContextStorage.getCurrentSessionId();

    if (!currentSessionId) {
      navigate("/login", { replace: true });
      return;
    }

    const scopeContext = ScopeContextStorage.getScopeContext(currentSessionId);

    if (!scopeContext) {
      navigate("/account-holder-selection", {
        state: { sessionId: currentSessionId },
        replace: true,
      });
      return;
    }

    const missing = ScopeContextStorage.needsScopeSelection(scopeContext);

    if (missing === "holder") {
      navigate("/account-holder-selection", {
        state: { sessionId: currentSessionId },
        replace: true,
      });
      return;
    }

    if (missing === "account") {
      navigate("/account-selection", {
        state: { sessionId: currentSessionId, scopeContext },
        replace: true,
      });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    const mediaQueryList = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const closeOnDesktop = (event: MediaQueryListEvent) => {
      if (event.matches) setSidebarOpen(false);
    };
    mediaQueryList.addEventListener("change", closeOnDesktop);
    return () => mediaQueryList.removeEventListener("change", closeOnDesktop);
  }, []);

  const exitThroughDrawer = (to: string, state?: unknown) => {
    pendingExitRef.current = { to, state };
    setSidebarOpen(false);
  };

  const navigatePendingExit = () => {
    const pendingExit = pendingExitRef.current;
    if (!pendingExit) return;
    pendingExitRef.current = null;
    navigate(pendingExit.to, { state: pendingExit.state });
  };

  return (
    <div className="flex min-h-screen bg-background">
      <UserSettingSync />
      <Sidebar />
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent
          side="left"
          className="w-64 p-0 gap-0 flex flex-col bg-sidebar border-sidebar-border md:hidden"
          onCloseAutoFocus={navigatePendingExit}
        >
          <SheetTitle className="sr-only">{t("sidebar.menu")}</SheetTitle>
          <SidebarContent
            onNavigate={() => setSidebarOpen(false)}
            onExitNavigate={exitThroughDrawer}
          />
        </SheetContent>
      </Sheet>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="flex justify-between items-center gap-2 h-16 px-4 md:px-8 flex-shrink-0 max-md:sticky max-md:top-0 max-md:z-40 max-md:bg-background max-md:border-b max-md:border-border/50">
          <div className="flex items-center gap-3 min-w-0 md:hidden">
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0"
              data-testid="sidebar-toggle"
              aria-label={t("sidebar.openMenu")}
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <Logo className="min-w-0" />
          </div>
          <div className="flex items-center gap-2 ml-auto shrink-0">
            <NotificationBell />
            <UserMenu />
          </div>
        </header>
        <Outlet />
      </div>
    </div>
  );
};
